import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Image as ImageIcon } from '@material-ui/icons';
import styled from 'styled-components';
import { SurfaceCard } from '../../../shared/ui';
import { lightTheme as theme } from '../../../shared/theme';

const PlaceholderImageIcon = styled(ImageIcon)`
    font-size: 28px !important;
`;

const SectionCard = styled(SurfaceCard)`
    width: 100%;
`;

const CardTitle = styled.h3`
    margin: 0 0 14px;
    font-size: 16px;
    font-weight: 700;
    color: ${theme.colors.primary};
`;

const PhotosGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 12px;
`;

const EmptyText = styled.p`
    margin: 0;
    color: ${theme.colors.textMuted};
    font-size: 14px;
`;

const PhotoButton = styled.button`
    width: 100%;
    border: 1px solid ${theme.colors.gray200};
    background: ${theme.colors.gray50};
    border-radius: 10px;
    aspect-ratio: 1 / 1;
    cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
    padding: 0;
    overflow: hidden;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: border-color 0.2s ease, transform 0.2s ease;

    &:hover {
        border-color: ${({ $clickable }) => ($clickable ? theme.colors.action : theme.colors.gray200)};
        transform: ${({ $clickable }) => ($clickable ? 'translateY(-2px)' : 'none')};
    }
`;

const PhotoImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const Placeholder = styled.div`
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    color: ${theme.colors.textMuted};
    font-size: 11px;
    text-align: center;
    padding: 8px;
`;

function EquipmentPhotoTile({ photo, onPhotoClick, fallbackAltText }) {
    const [hasImageError, setHasImageError] = useState(false);

    const imageUrl = useMemo(
        () => photo?.nome_arquivo || photo?.file_url || null,
        [photo]
    );

    const hasImage = Boolean(imageUrl) && !hasImageError;

    return (
        <PhotoButton
            type='button'
            onClick={() => {
                if (hasImage) onPhotoClick(imageUrl);
            }}
            $clickable={hasImage}
        >
            {hasImage ? (
                <PhotoImage
                    src={imageUrl}
                    alt={photo?.nome || fallbackAltText}
                    onError={() => setHasImageError(true)}
                />
            ) : (
                <Placeholder>
                    <PlaceholderImageIcon />
                    <span>{photo?.nome || fallbackAltText}</span>
                </Placeholder>
            )}
        </PhotoButton>
    );
}

EquipmentPhotoTile.propTypes = {
    photo: PropTypes.object,
    onPhotoClick: PropTypes.func.isRequired,
    fallbackAltText: PropTypes.string.isRequired,
};

EquipmentPhotoTile.defaultProps = {
    photo: null,
};

function EquipmentPhotosSection({
    sectionTitle,
    photos,
    emptyMessage,
    onPhotoClick,
    fallbackAltText,
}) {
    if (!Array.isArray(photos) || photos.length === 0) {
        return (
            <SectionCard padding={20}>
                <CardTitle>{sectionTitle}</CardTitle>
                <EmptyText>{emptyMessage}</EmptyText>
            </SectionCard>
        );
    }

    return (
        <SectionCard padding={20}>
            <CardTitle>{sectionTitle}</CardTitle>
            <PhotosGrid>
                {photos.map((photo, index) => (
                    <EquipmentPhotoTile
                        key={`${photo?.nome_arquivo || photo?.nome || 'photo'}-${index}`}
                        photo={photo}
                        onPhotoClick={onPhotoClick}
                        fallbackAltText={fallbackAltText}
                    />
                ))}
            </PhotosGrid>
        </SectionCard>
    );
}

EquipmentPhotosSection.propTypes = {
    sectionTitle: PropTypes.string.isRequired,
    photos: PropTypes.arrayOf(PropTypes.object),
    emptyMessage: PropTypes.string.isRequired,
    onPhotoClick: PropTypes.func.isRequired,
    fallbackAltText: PropTypes.string.isRequired,
};

EquipmentPhotosSection.defaultProps = {
    photos: [],
};

export default EquipmentPhotosSection;
